import { useEffect, useMemo, useState } from 'react'
import { PageHeader } from '../../../components/PageHeader'
import { Avatar } from '../../../components/Avatar'
import { BackgroundTitle } from '../../../components/BackgroundTitle'
import { useAuth } from '../../../lib/auth'
import { useMutedIds, MUTED_PLACEHOLDER } from '../../../lib/api/mutes'
import { supabase } from '../../../lib/supabase'
import { readError } from '../../../lib/errors'
import {
  buildPromptText, castThreeStepWeeklyVote,
  fetchMyThreeStepWeeklyVotes, fetchThreeStepWeeklyEntries,
  reportThreeStepWeeklyEntry, weekLabel,
  type ThreeStepWeeklyEntryRow, type ThreeStepWeeklyEventRow,
} from '../api/weekly'
import { useT } from '../../../i18n'

interface Props {
  eventId: string
  onBack: () => void
  /** Open the author's public profile when their name is tapped. */
  onOpenProfile?: (profileId: string) => void
}

/** Mirror of the DB-enforced cap. Keep in sync with cast_three_step_weekly_vote. */
const MAX_VOTES_PER_VOTER = 2

type EntryWithAuthor = ThreeStepWeeklyEntryRow & {
  author_name: string | null
  author_avatar: string | null
}

/**
 * View + vote on a past week's three-step solutions. Voting toggles
 * via cast_three_step_weekly_vote; the RPC enforces the 2-vote cap
 * and blocks self-votes.
 */
export function ThreeStepWeeklyVotingScreen({ eventId, onBack, onOpenProfile }: Props) {
  const t = useT()
  const { profile, session, exitGuestMode } = useAuth()
  const { mutedIds } = useMutedIds()
  const signedIn = Boolean(session && profile)
  // Same rule as the IET voting screen: anonymous guest sessions read
  // freely, but the vote RPC rejects them — so the button becomes a
  // sign-in affordance instead of erroring on tap.
  const isAnon = Boolean((session?.user as { is_anonymous?: boolean } | undefined)?.is_anonymous)
  const canVote = signedIn && !isAnon
  const [event, setEvent] = useState<ThreeStepWeeklyEventRow | null>(null)
  const [entries, setEntries] = useState<EntryWithAuthor[]>([])
  const [myVotes, setMyVotes] = useState<Set<string>>(new Set())
  const [expanded, setExpanded] = useState<string | null>(null)
  const [reported, setReported] = useState<Set<string>>(new Set())
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    async function load() {
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const evRes = await (supabase as any)
          .from('three_step_weekly_events').select('*').eq('id', eventId).single()
        if (evRes.error) throw evRes.error
        if (cancelled) return
        setEvent(evRes.data as ThreeStepWeeklyEventRow)

        const all = await fetchThreeStepWeeklyEntries(eventId)
        // Only show finalized entries that the author actually filled
        // out (skip empty rows from people who opened the editor and
        // never wrote anything).
        const hasContent = (e: ThreeStepWeeklyEntryRow) =>
          (e.step1_text?.trim().length ?? 0) > 0 ||
          (e.step2_text?.trim().length ?? 0) > 0 ||
          (e.step3_text?.trim().length ?? 0) > 0
        const visible = all.filter(e => e.is_final && hasContent(e))
        if (cancelled) return
        setEntries(visible)

        if (signedIn && profile) {
          const voted = await fetchMyThreeStepWeeklyVotes(eventId)
          if (!cancelled) setMyVotes(voted)
        }
      } catch (e) {
        if (!cancelled) setError(readError(e))
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [eventId, signedIn, profile?.id])

  async function onToggle(entry: EntryWithAuthor) {
    if (!profile || !canVote) return
    if (entry.profile_id === profile.id) return
    const already = myVotes.has(entry.id)
    if (!already && myVotes.size >= MAX_VOTES_PER_VOTER) {
      setError(t('You can vote on at most {n} entries per week.', { n: MAX_VOTES_PER_VOTER }))
      return
    }
    // Optimistic update.
    setMyVotes(prev => {
      const next = new Set(prev)
      if (already) next.delete(entry.id); else next.add(entry.id)
      return next
    })
    setEntries(prev => prev.map(e =>
      e.id === entry.id
        ? { ...e, total_votes: Math.max(0, e.total_votes + (already ? -1 : 1)) }
        : e
    ))
    try {
      const newTotal = await castThreeStepWeeklyVote(entry.id)
      setEntries(prev => prev.map(e =>
        e.id === entry.id ? { ...e, total_votes: newTotal } : e
      ))
    } catch (e) {
      // Roll back on failure.
      setMyVotes(prev => {
        const next = new Set(prev)
        if (already) next.add(entry.id); else next.delete(entry.id)
        return next
      })
      setEntries(prev => prev.map(en =>
        en.id === entry.id
          ? { ...en, total_votes: Math.max(0, en.total_votes + (already ? 1 : -1)) }
          : en
      ))
      setError(readError(e))
    }
  }

  async function onReport(entryId: string) {
    try {
      await reportThreeStepWeeklyEntry(entryId)
      setReported(prev => {
        const next = new Set(prev)
        next.add(entryId)
        return next
      })
    } catch (e) {
      setError(readError(e))
    }
  }

  const sorted = useMemo(
    () => [...entries].sort((a, b) => b.total_votes - a.total_votes),
    [entries],
  )

  const myVoteCount = sorted.filter(e => myVotes.has(e.id)).length
  const voteableCount = sorted.filter(e => e.profile_id !== profile?.id).length
  const atCap = myVoteCount >= MAX_VOTES_PER_VOTER

  return (
    <div className="screen">
      <BackgroundTitle text={t('Vote')} color="#0EAE93" />
      <PageHeader
        title={t('Vote')}
        subtitle={event ? weekLabel(event.week_start) : '…'}
        onBack={onBack}
      />

      {/* Prompt banner */}
      {event && (
        <div className="px-5 pt-3 pb-1 relative z-10">
          <div className="bg-game-three-step-light border-2 border-game-three-step-dark
                          rounded-2xl shadow-card p-3">
            <p className="text-[10px] font-semibold text-game-three-step-dark
                          uppercase tracking-widest mb-1">
              {t('The prompt')}
            </p>
            <p className="font-display text-base font-bold text-brand-text leading-snug">
              {buildPromptText(event.action_a, event.action_b, t)}
            </p>
          </div>
        </div>
      )}

      {/* Vote progress */}
      {voteableCount > 0 && (
        <div className="px-5 py-3 border-b border-brand-border relative z-10 bg-brand-bg">
          <div className="flex items-center justify-between text-xs text-brand-muted mb-1.5">
            <span>{t('Your votes')}</span>
            <span>{myVoteCount}/{MAX_VOTES_PER_VOTER}</span>
          </div>
          <div className="h-1.5 rounded-full bg-brand-elevated overflow-hidden">
            <div
              className="h-full bg-game-three-step-dark rounded-full transition-all duration-500"
              style={{ width: `${(myVoteCount / MAX_VOTES_PER_VOTER) * 100}%` }}
            />
          </div>
        </div>
      )}

      <div className="px-5 py-4 space-y-3 overflow-y-auto pb-10 relative z-10">
        {error && (
          <div className="card p-3 bg-brand-danger/10 border border-brand-danger/30">
            <p className="text-sm text-brand-danger">{error}</p>
          </div>
        )}

        {loading && (
          <>
            <div className="h-24 rounded-2xl bg-brand-elevated animate-pulse" />
            <div className="h-24 rounded-2xl bg-brand-elevated animate-pulse" />
            <div className="h-24 rounded-2xl bg-brand-elevated animate-pulse" />
          </>
        )}

        {!loading && sorted.length === 0 && (
          <div className="card p-5 text-center">
            <p className="font-semibold text-brand-text">{t('Nothing to vote on yet')}</p>
            <p className="text-sm text-brand-muted mt-1">
              {t("Entries from this week will appear here once they're finalized.")}
            </p>
          </div>
        )}

        {sorted.map(entry => {
          const isOwn = entry.profile_id === profile?.id
          const muted = mutedIds.has(entry.profile_id)
          const voted = myVotes.has(entry.id)
          const isExpanded = expanded === entry.id
          const author = entry.author_name ?? t('Player')
          const s1 = muted ? MUTED_PLACEHOLDER : (entry.step1_text || '…')
          const s2 = muted ? MUTED_PLACEHOLDER : (entry.step2_text || '…')
          const s3 = muted ? MUTED_PLACEHOLDER : (entry.step3_text || '…')
          const previewLine = muted
            ? MUTED_PLACEHOLDER
            : (entry.step1_text || entry.step2_text || entry.step3_text || '')
          return (
            <div key={entry.id}
                 className={`bg-game-three-step-light border-2 border-game-three-step-dark
                             rounded-2xl shadow-card overflow-hidden transition-all
                             ${voted ? 'ring-2 ring-game-three-step-dark/50' : ''}`}>
              <button
                onClick={() => setExpanded(isExpanded ? null : entry.id)}
                className="w-full p-4 flex items-start gap-3 text-left"
              >
                <Avatar
                  name={author}
                  avatarUrl={entry.author_avatar}
                  size="md"
                  className="shrink-0 mt-0.5"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    {onOpenProfile && !isOwn ? (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); onOpenProfile(entry.profile_id) }}
                        className="font-semibold text-brand-text text-sm truncate
                                   underline underline-offset-2 hover:text-game-three-step-dark"
                      >
                        {author}
                      </button>
                    ) : (
                      <span className="font-semibold text-brand-text text-sm truncate">
                        {author}
                      </span>
                    )}
                    {isOwn && (
                      <span className="text-[10px] bg-white/70 text-game-three-step-dark
                                       px-2 py-0.5 rounded-full font-medium">
                        {t('You')}
                      </span>
                    )}
                    {voted && (
                      <span className="text-xs text-game-three-step-dark font-semibold">
                        {t('✓ Voted')}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-brand-muted line-clamp-2 mt-0.5">
                    {previewLine || t('(no entry yet)')}
                  </p>
                </div>
                <div className="shrink-0 flex flex-col items-center gap-0.5">
                  <span className="font-display font-bold text-game-three-step-dark text-lg">
                    {entry.total_votes}
                  </span>
                  <span className="text-[10px] text-brand-muted">{t('votes')}</span>
                </div>
              </button>

              {/* Expanded — render the three steps as a numbered list. */}
              {isExpanded && (
                <div className="border-t border-game-three-step-dark/40 px-4 py-3 bg-white/50">
                  <ol className="space-y-2 text-sm text-brand-text">
                    <li className="flex gap-3">
                      <span className="font-display font-bold text-game-three-step-dark
                                       w-5 shrink-0">1.</span>
                      <span className="leading-relaxed whitespace-pre-wrap">
                        {s1}
                      </span>
                    </li>
                    <li className="flex gap-3">
                      <span className="font-display font-bold text-game-three-step-dark
                                       w-5 shrink-0">2.</span>
                      <span className="leading-relaxed whitespace-pre-wrap">
                        {s2}
                      </span>
                    </li>
                    <li className="flex gap-3">
                      <span className="font-display font-bold text-game-three-step-dark
                                       w-5 shrink-0">3.</span>
                      <span className="leading-relaxed whitespace-pre-wrap">
                        {s3}
                      </span>
                    </li>
                  </ol>
                  {!isOwn && (
                    <div className="flex justify-end mt-2">
                      {reported.has(entry.id) ? (
                        <span className="text-[11px] text-brand-muted">{t('Reported')}</span>
                      ) : (
                        <button
                          onClick={() => onReport(entry.id)}
                          className="text-[11px] text-brand-muted underline-offset-2
                                     underline hover:text-brand-danger"
                        >
                          {t('Flag')}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}

              {!isOwn && canVote && (
                <div className="px-4 pb-4 pt-1">
                  <button
                    onClick={() => onToggle(entry)}
                    disabled={!voted && atCap}
                    className={`w-full py-3 rounded-2xl font-semibold text-sm
                                flex items-center justify-center gap-2
                                active:scale-95 transition-all border-2
                                disabled:opacity-40 disabled:cursor-not-allowed
                                ${voted
                                  ? 'bg-game-three-step-dark/10 text-game-three-step-dark border-game-three-step-dark/40 shadow-xs'
                                  : 'bg-white/85 border-game-three-step-dark text-brand-text shadow-card'}`}
                  >
                    {voted
                      ? t('✅ Solid solution · {n} total', { n: entry.total_votes })
                      : atCap
                        ? t('{n}-vote limit reached', { n: MAX_VOTES_PER_VOTER })
                        : t('👍 Solid solution')}
                  </button>
                </div>
              )}

              {!isOwn && !canVote && (
                <div className="px-4 pb-4 pt-1">
                  <button
                    onClick={exitGuestMode}
                    className="w-full py-3 rounded-2xl font-semibold text-sm
                               bg-white/85 border-2 border-brand-border text-brand-muted
                               active:scale-95 transition-all shadow-card"
                  >
                    {t('Sign in to vote')}
                  </button>
                </div>
              )}

              {isOwn && (
                <div className="px-4 pb-4 pt-1">
                  <div className="w-full py-3 rounded-2xl bg-brand-elevated text-brand-muted
                                  font-medium text-sm text-center">
                    {t("Your entry. You can't vote for yourself.")}
                  </div>
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
